import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { SiDotnet, SiReact, SiLaravel, SiPython, SiNodedotjs } from 'react-icons/si';
import { useTranslation } from 'react-i18next';

const Projects = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const projects = [
    {
      titleKey: 'items.taxiApp.title',
      descriptionKey: 'items.taxiApp.description',
      technologies: ['.NET', 'React Native', 'Azure', 'Maps API'],
      status: 'In Development',
      highlightsKey: 'items.taxiApp.highlights',
      icon: SiDotnet,
      color: '#512BD4',
    },
    {
      titleKey: 'items.liveStreaming.title',
      descriptionKey: 'items.liveStreaming.description',
      technologies: ['Laravel', 'React', 'WebSockets', 'Redis'],
      status: 'Production',
      highlightsKey: 'items.liveStreaming.highlights',
      icon: SiReact,
      color: '#61DAFB',
    },
    {
      titleKey: 'items.lms.title',
      descriptionKey: 'items.lms.description',
      technologies: ['Laravel', 'Node.js', 'React', 'Docker'],
      status: 'Production',
      highlightsKey: 'items.lms.highlights',
      icon: SiLaravel,
      color: '#FF2D20',
    },
    {
      titleKey: 'items.liveCommerce.title',
      descriptionKey: 'items.liveCommerce.description',
      technologies: ['Laravel', 'React', 'MySQL', 'Payment APIs'],
      status: 'Production',
      highlightsKey: 'items.liveCommerce.highlights',
      icon: SiReact,
      color: '#61DAFB',
    },
    {
      titleKey: 'items.mealControl.title',
      descriptionKey: 'items.mealControl.description',
      technologies: ['Python', 'TensorFlow', 'OpenCV', 'Laravel'],
      status: 'Production',
      highlightsKey: 'items.mealControl.highlights',
      icon: SiPython,
      color: '#3776AB',
    },
    {
      titleKey: 'items.radioSystem.title',
      descriptionKey: 'items.radioSystem.description',
      technologies: ['Node.js', 'React', 'MongoDB', 'FFmpeg'],
      status: 'Production',
      highlightsKey: 'items.radioSystem.highlights',
      icon: SiNodedotjs,
      color: '#339933',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section id="projects" className="py-20 relative">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent"
          >
            {t('projects.title')}
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => {
              const Icon = project.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.03, y: -5 }}
                  className="glass p-6 rounded-2xl glow-hover relative overflow-hidden group"
                >
                  {/* Background gradient */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-full filter blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"
                    style={{ background: project.color }}
                  ></div>

                  {/* Status badge */}
                  <div
                    className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
                      project.status === 'In Development'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-green-500 text-black'
                    }`}
                  >
                    {project.status === 'In Development' ? t('projects.inDevelopment') : t('projects.production')}
                  </div>

                  {/* Project icon */}
                  <div className="mb-4">
                    <Icon className="text-5xl" style={{ color: project.color }} />
                  </div>

                  {/* Project title */}
                  <h3 className="text-2xl font-bold text-blue-400 mb-3">{t(`projects.${project.titleKey}`)}</h3>

                  {/* Project description */}
                  <p className="text-gray-300 mb-4 leading-relaxed">{t(`projects.${project.descriptionKey}`)}</p>

                  {/* Highlights */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-blue-400 mb-2">{t('projects.keyFeatures')}:</p>
                    <ul className="space-y-1">
                      {(() => {
                        const translationKey = `projects.${project.highlightsKey}`;
                        const highlights = t(translationKey, { returnObjects: true });
                        // Verifica se é um array válido
                        if (Array.isArray(highlights) && highlights.length > 0) {
                          return highlights.slice(0, 3);
                        }
                        // Fallback: retorna array vazio se não for array válido
                        return [];
                      })().map((highlight, idx) => (
                        <li key={idx} className="text-sm text-gray-400 flex items-start">
                          <span className="text-blue-500 mr-2">▸</span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-500 bg-opacity-20 text-blue-400 px-3 py-1 rounded-full text-xs font-semibold"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
